import { useCallback, useEffect, useRef, useState } from "react";

export interface PagedResult<T> {
  data: T[];
  hasMore: boolean;
}

// Distinct from any real `deps` value — marks "the fetch effect has never
// run for this hook instance yet", so a genuine first run can be told apart
// from a later run whose deps happen to be value-equal to last time. Same
// sentinel pattern UploadHistoryContext's own hydration effect already uses
// (PROFILE_ID_NEVER_HYDRATED) for the identical reason: React's Strict Mode
// double-invokes effects in dev, and Vite's Fast Refresh can remount a
// provider mid-session while editing — neither is a REAL scope change, so
// neither should nuke already-loaded data.
const NEVER_HYDRATED = Symbol("usePaginatedList-never-hydrated");

function depsChanged(prev: unknown[], next: unknown[]): boolean {
  if (prev.length !== next.length) return true;
  return prev.some((value, i) => value !== next[i]);
}

/**
 * Generic "fetch the first page up front, then fetch more on demand and
 * append" state machine — the same fetchedCountRef/hasMore/isLoadingMore
 * shape UploadHistoryContext already hand-rolls for upload history (see
 * loadMoreUploads/appendUploadPage there), extracted here so a new paginated
 * list (e.g. imaging studies) reuses it instead of re-deriving the same
 * offset-tracking and dedup-on-append logic.
 *
 * Re-fetches the first page from scratch whenever `deps` changes (e.g.
 * switching Dedicated Profile) — same "never show the previous scope's
 * stale data while the new page loads" rule every other fetch-once hook in
 * this app follows.
 */
export function usePaginatedList<T>(
  fetchPage: (offset: number, limit: number) => Promise<PagedResult<T>>,
  options: {
    pageSize: number;
    getId: (item: T) => string;
    deps?: unknown[];
    /** Shown on a failed fetch — defaults to a generic message since callers
     *  usually want their own domain-specific wording (e.g. "Couldn't load
     *  your imaging studies."). */
    errorMessage?: string;
  },
) {
  const { pageSize, getId, deps = [], errorMessage = "Couldn't load. Please try again later." } = options;

  const [items, setItems] = useState<T[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshToken, setRefreshToken] = useState(0);

  // How many rows have been fetched from the server so far — doubles as the
  // next page's offset. A ref (not state) because updating it must never
  // itself trigger a re-render/re-fetch.
  const fetchedCountRef = useRef(0);
  // Kept current without adding `fetchPage` itself to the effect/callback
  // dependency arrays below — a caller that doesn't memoize its fetchPage
  // function (a fresh closure every render) would otherwise re-run the
  // initial fetch on every render instead of only on `deps`/refresh.
  const fetchPageRef = useRef(fetchPage);
  fetchPageRef.current = fetchPage;

  const refresh = useCallback(() => setRefreshToken((t) => t + 1), []);

  // The `deps` this hydration effect last ran for — compared BY VALUE
  // (depsChanged), not by "did the effect fire again" — see NEVER_HYDRATED's
  // own docstring above for why that distinction matters.
  const previousDepsRef = useRef<unknown[] | typeof NEVER_HYDRATED>(NEVER_HYDRATED);

  useEffect(() => {
    let cancelled = false;

    // A REAL scope change (e.g. switching Dedicated Profile) — the deps
    // actually changed value since this effect last ran, not just "the
    // effect fired again" (a plain refresh() call, React Strict Mode's dev
    // double-invoke, or a Fast Refresh remount all re-run this effect
    // without `deps` truly changing). Only a genuine scope change may show
    // the empty state while the new page loads; anything else keeps
    // whatever was already on screen until fresh data actually arrives, so
    // a benign re-run — or a fetch that then fails — can never blank out an
    // already-successfully-loaded list.
    const isRealScopeChange =
      previousDepsRef.current !== NEVER_HYDRATED && depsChanged(previousDepsRef.current as unknown[], deps);
    previousDepsRef.current = deps;

    setIsLoading(true);
    if (isRealScopeChange) {
      setItems([]);
      fetchedCountRef.current = 0;
      setHasMore(false);
    }
    fetchPageRef
      .current(0, pageSize)
      .then((page) => {
        if (cancelled) return;
        setItems(page.data);
        fetchedCountRef.current = page.data.length;
        setHasMore(page.hasMore);
        setError(null);
      })
      .catch(() => {
        if (cancelled) return;
        // Deliberately does NOT clear items — a failed fetch keeps showing
        // whatever was already loaded (with `error` set alongside it)
        // instead of blanking a previously-working list, same as
        // UploadHistoryContext's own swallow-and-keep-stale convention.
        setError(errorMessage);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshToken, pageSize, ...deps]);

  const loadMore = useCallback(() => {
    if (isLoadingMore || !hasMore) return;
    setIsLoadingMore(true);
    fetchPageRef
      .current(fetchedCountRef.current, pageSize)
      .then((page) => {
        setItems((prev) => {
          const existingIds = new Set(prev.map(getId));
          const appended = page.data.filter((item) => !existingIds.has(getId(item)));
          return [...prev, ...appended];
        });
        fetchedCountRef.current += page.data.length;
        setHasMore(page.hasMore);
      })
      // Leaves hasMore as-is on failure — same convention as
      // UploadHistoryContext's loadMoreUploads — so scrolling near the
      // bottom again simply retries instead of silently going stuck.
      .catch(() => {})
      .finally(() => setIsLoadingMore(false));
  }, [hasMore, isLoadingMore, pageSize, getId]);

  return { items, isLoading, isLoadingMore, hasMore, error, loadMore, refresh };
}
